import React from 'react';
import { Dialog } from 'primereact/dialog';
import { GlobalButton } from './GlobalComponents/GlobalButton';

interface CustomConfirmDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: () => void;
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  severity?: 'danger' | 'warning' | 'info';
  icon?: string;
}

const CustomConfirmDialog: React.FC<CustomConfirmDialogProps> = ({
  visible,
  onHide,
  onConfirm,
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  severity = 'danger',
  icon = 'pi pi-exclamation-triangle'
}) => {
  const getSeverityColor = () => {
    switch (severity) {
      case 'danger':
        return {
          gradient: 'linear-gradient(135deg, #d32f2f 0%, #c62828 50%, #b71c1c 100%)',
          background: '#d32f2f',
          light: 'rgba(211, 47, 47, 0.08)',
          iconBg: 'rgba(211, 47, 47, 0.12)',
          buttonVariant: 'danger' as const
        };
      case 'warning':
        return {
          gradient: 'linear-gradient(135deg, #f57c00 0%, #ef6c00 50%, #e65100 100%)',
          background: '#f57c00',
          light: 'rgba(245, 124, 0, 0.08)',
          iconBg: 'rgba(245, 124, 0, 0.12)',
          buttonVariant: 'warning' as const
        };
      case 'info':
        return {
          gradient: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
          background: '#0f3d3e',
          light: 'rgba(15, 61, 62, 0.08)',
          iconBg: 'rgba(15, 61, 62, 0.12)',
          buttonVariant: 'primary' as const
        };
      default:
        return {
          gradient: 'linear-gradient(135deg, #d32f2f 0%, #c62828 50%, #b71c1c 100%)',
          background: '#d32f2f',
          light: 'rgba(211, 47, 47, 0.08)',
          iconBg: 'rgba(211, 47, 47, 0.12)',
          buttonVariant: 'danger' as const
        };
    }
  };

  const colors = getSeverityColor();

  const handleConfirm = () => {
    onConfirm();
    onHide();
  };

  return (
    <>
      <style>
        {`
          .custom-confirm-dialog {
            border-radius: 20px !important;
            box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15) !important;
            overflow: hidden !important;
            max-width: 440px !important;
            width: 90% !important;
            border: none !important;
          }
          
          .custom-confirm-dialog .p-dialog-header {
            padding: 0 !important;
            border: none !important;
            background: transparent !important;
          }
          
          .custom-confirm-dialog .p-dialog-content {
            padding: 0 !important;
            border: none !important;
          }
          
          .custom-confirm-dialog .p-dialog-header-close {
            display: none !important;
          }
        `}
      </style>
      
      <Dialog
        visible={visible}
        onHide={onHide}
        modal
        dismissableMask
        className="custom-confirm-dialog"
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0
        }}>
          {/* Header with Gradient */}
          <div style={{
            background: colors.gradient,
            padding: '28px 28px 20px 28px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative Circles */}
            <div style={{
              position: 'absolute',
              top: '-30px',
              right: '-30px',
              width: '100px',
              height: '100px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              filter: 'blur(30px)'
            }} />
            
            <div style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '14px'
            }}>
              {/* Icon */}
              <div style={{
                width: '64px',
                height: '64px',
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255, 255, 255, 0.25)'
              }}>
                <i className={icon} style={{
                  fontSize: '1.6rem',
                  color: 'white'
                }}></i>
              </div>
              
              {/* Title */}
              <h2 style={{
                margin: 0,
                fontSize: '1.25rem',
                fontWeight: '700',
                color: 'white',
                letterSpacing: '-0.5px'
              }}>
                {title}
              </h2>
            </div>
          </div>

          {/* Body */}
          <div style={{
            padding: '28px',
            background: 'white'
          }}>
            {/* Message */}
            <div style={{
              marginBottom: '24px'
            }}>
              <p style={{
                margin: 0,
                fontSize: '0.9rem',
                color: '#666',
                lineHeight: '1.6',
                textAlign: 'center'
              }}>
                {message}
              </p>
            </div>

            {/* Buttons */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px'
            }}>
              <GlobalButton
                label={cancelLabel}
                onClick={onHide}
                variant="outline"
                size="medium"
                icon="pi pi-times"
                iconPos="left"
              />
              
              <GlobalButton
                label={confirmLabel}
                onClick={handleConfirm}
                variant={colors.buttonVariant}
                size="medium"
                icon="pi pi-check"
                iconPos="left"
              />
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CustomConfirmDialog;